import type { AxiosInstance } from 'axios';
import type { Logger } from '@wg/api-client';
import type { StorageAdapter } from '../storage';
import type {
  AppInfo,
  AuthEvent,
  AuthEventListener,
  GuestSessionMetadata,
  RegisterPayload,
  Session,
  SessionInfo,
  SigninPayload,
} from './types';
import { sessionInfoFromToken } from './jwt';

const AUTH_API = '/functions/v1/auth';
const REFRESH_BUFFER_SECS = 60;
const PREEMPTIVE_REFRESH_SECS = 300;

// Refresh retry policy:
//   - 4xx from auth server  → likely invalid refresh token. Retry a few times
//                             with backoff before clearing (server may have
//                             hiccuped; we want to be *sure* before logging
//                             the user out).
//   - 5xx / timeout / network → transient. Retry generously, never clear.
// Phones on flaky cell can timeout or hit 5xx bursts for minutes. Clearing
// the session in those cases wipes guest state and forces re-onboarding.
const MAX_REFRESH_4XX_RETRIES = 3;
const MAX_REFRESH_TRANSIENT_RETRIES = 8;
const BASE_BACKOFF_MS = 500;
const MAX_BACKOFF_MS = 5000;
// Wall-clock ceiling on the whole retry ladder. The retry counts alone are
// NOT a bound on caller-visible latency: the http client awaits
// getAuthToken() inside its REQUEST interceptor, and axios' `timeout` only
// starts once a request is dispatched. So a stuck ladder (8 transient
// retries × the unauth client's timeout, plus backoff) parks the caller's
// request in "pending" for minutes with no timeout ever firing — and since
// `refreshing` is shared, every other authed request queues behind it. The
// retry counts still cap attempts on a fast-failing network; this caps the
// slow-failing one.
const REFRESH_DEADLINE_MS = 12_000;

const now = () => Math.floor(Date.now() / 1000);
const asAbsolute = (exp: number) => (exp < 86_400 ? now() + exp : exp);
const jitter = (ms: number) => ms * (0.5 + Math.random());
const backoffMs = (attempt: number) =>
  Math.min(BASE_BACKOFF_MS * Math.pow(2, attempt), MAX_BACKOFF_MS);
const sleep = (ms: number) => new Promise(r => setTimeout(r, ms));

export interface AuthCoreDeps {
  /** Authenticated axios client (Bearer-injected via HttpClientDeps.getAuthToken). */
  httpClient: AxiosInstance;
  /** Unauthenticated axios client. */
  unauthClient: AxiosInstance;
  storage: StorageAdapter;
  logger: Logger;
  /**
   * Storage key for the session blob. Web: 'auth' (single-env). RN: 'auth_<env>'
   * where env is dev/local/prod. Pass whatever the client decides — core stays
   * agnostic.
   */
  storageKey: string;
  /**
   * Other storage keys from the same family that should be cleaned up on
   * explicit logout (RN had auth_dev/auth_local/auth_prod accumulating).
   * Optional — web typically doesn't need it.
   */
  siblingStorageKeys?: string[];
  /** Returned by getAppInfo() for password-reset email metadata. */
  getAppInfo: () => AppInfo;
}

export interface AuthCore {
  // Token retrieval
  getAuthToken(): Promise<string | null>;
  tryRefreshAuthToken(): Promise<string | null>;
  getSessionInfo(): Promise<SessionInfo>;

  // Session lifecycle
  signin(payload: SigninPayload): Promise<unknown>;
  register(payload: RegisterPayload): Promise<unknown>;
  createGuestSession(metadata?: GuestSessionMetadata): Promise<unknown>;
  setSessionFromExternal(tokens: {
    accessToken: string;
    refreshToken: string;
    expiresAt: number;
  }): Promise<void>;
  logout(): Promise<void>;
  deleteAccount(): Promise<void>;
  clearAuthSession(): Promise<void>;

  // Password reset
  requestPasswordReset(
    emailAddress: string,
    language?: string,
  ): Promise<unknown>;
  confirmPasswordReset(payload: {
    code: string;
    newPassword: string;
  }): Promise<unknown>;

  // Events
  onAuthEvent(listener: AuthEventListener): () => void;
}

/**
 * Build a singleton-style auth state machine. Hold one per client (the http
 * client created via createHttpClient already has getAuthToken/tryRefreshAuthToken
 * wired to point back at this core).
 *
 * Wiring on the consumer side:
 *   const auth = createAuthCore({ ...partial deps... });
 *   const { httpClient, unauthClient } = createHttpClient({
 *     ...,
 *     getAuthToken: () => auth.getAuthToken(),
 *     tryRefreshAuthToken: () => auth.tryRefreshAuthToken(),
 *   });
 *   auth.attachClients({ httpClient, unauthClient });
 *
 * Two-phase init solves the chicken-and-egg between http client and auth core.
 */
export function createAuthCore(
  deps: Omit<AuthCoreDeps, 'httpClient' | 'unauthClient'>,
): AuthCore & {
  attachClients(clients: {
    httpClient: AxiosInstance;
    unauthClient: AxiosInstance;
  }): void;
} {
  const {
    storage,
    logger,
    storageKey,
    siblingStorageKeys = [],
    getAppInfo,
  } = deps;

  let session: Session | null = null;
  let httpClient: AxiosInstance | null = null;
  let unauthClient: AxiosInstance | null = null;

  // Event system for React land
  const listeners = new Set<AuthEventListener>();
  const emit = (event: AuthEvent) => {
    listeners.forEach(l => {
      try {
        l(event);
      } catch {
        /* noop */
      }
    });
  };

  function requireClients(): {
    httpClient: AxiosInstance;
    unauthClient: AxiosInstance;
  } {
    if (!httpClient || !unauthClient) {
      throw new Error(
        '[auth] http clients not attached — call attachClients() after createHttpClient',
      );
    }
    return { httpClient, unauthClient };
  }

  // ────────────────────────────────────────────────
  // Persistence
  async function persist(s: Session | null) {
    session = s;
    if (s) {
      storage.setString(storageKey, JSON.stringify(s));
    } else {
      storage.remove(storageKey);
    }
    emit('sessionUpdated');
  }

  let hydrated = false;
  async function hydrateOnce() {
    if (hydrated || session) {
      hydrated = true;
      return;
    }
    const raw = storage.getString(storageKey);
    if (!raw) {
      hydrated = true;
      return;
    }
    try {
      const parsed = JSON.parse(raw) as Session;
      if (!parsed.accessToken || !parsed.refreshToken || !parsed.expiresAt) {
        throw new Error('Invalid session payload');
      }
      parsed.expiresAt = asAbsolute(parsed.expiresAt);
      session = parsed;
    } catch (error) {
      logger.error('[auth] Error parsing session data', error);
      await persist(null);
    }
    hydrated = true;
  }

  // ────────────────────────────────────────────────
  // Refresh
  const valid = () =>
    session && session.expiresAt > now() + REFRESH_BUFFER_SECS;
  const nearEnd = () =>
    session && session.expiresAt <= now() + PREEMPTIVE_REFRESH_SECS;

  let refreshing: Promise<string | null> | null = null;

  async function refresh(): Promise<string | null> {
    if (refreshing) return refreshing;
    if (!session?.refreshToken) return null;
    const { unauthClient } = requireClients();

    refreshing = (async () => {
      const deadlineAt = Date.now() + REFRESH_DEADLINE_MS;
      try {
        let fourXxCount = 0;
        let transientCount = 0;
        while (true) {
          try {
            const { data } = await unauthClient.post(
              `${AUTH_API}/token/refresh`,
              { refreshToken: session!.refreshToken },
            );
            const next: Session = {
              accessToken: data.access_token,
              refreshToken: data.refresh_token,
              expiresAt: asAbsolute(Number(data.expires_at)),
            };
            await persist(next);
            return next.accessToken;
          } catch (err: unknown) {
            const e = err as { response?: { status?: number } };
            const status = e?.response?.status;
            const is4xx =
              typeof status === 'number' && status >= 400 && status < 500;

            if (is4xx) {
              fourXxCount++;
              logger.warn(
                `[auth.refresh] 4xx (status=${status}) attempt ${fourXxCount}/${MAX_REFRESH_4XX_RETRIES}`,
              );
              if (fourXxCount >= MAX_REFRESH_4XX_RETRIES) {
                logger.warn(
                  '[auth.refresh] Exhausted 4xx retries — refresh token invalid, clearing session',
                );
                await persist(null);
                return null;
              }
            } else {
              transientCount++;
              logger.warn(
                `[auth.refresh] Transient (status=${status ?? 'none'}) attempt ${transientCount}/${MAX_REFRESH_TRANSIENT_RETRIES}`,
              );
              if (transientCount >= MAX_REFRESH_TRANSIENT_RETRIES) {
                logger.warn(
                  '[auth.refresh] Exhausted transient retries — keeping existing session',
                );
                return session?.accessToken ?? null;
              }
            }
            const totalAttempt = fourXxCount + transientCount - 1;
            const waitMs = jitter(backoffMs(totalAttempt));
            // Give up on the wall clock, not just the attempt count. Returning
            // the existing token (or null) lets the caller's request finally
            // dispatch and fail fast on its own timeout instead of hanging.
            if (Date.now() + waitMs >= deadlineAt) {
              logger.warn(
                `[auth.refresh] Deadline reached (${REFRESH_DEADLINE_MS}ms) — releasing waiters`,
              );
              return session?.accessToken ?? null;
            }
            await sleep(waitMs);
          }
        }
      } finally {
        refreshing = null;
      }
    })();
    return refreshing;
  }

  async function getAuthToken(): Promise<string | null> {
    await hydrateOnce();
    if (valid()) {
      if (nearEnd()) {
        refresh().catch(() => {});
      }
      return session!.accessToken;
    }
    return refresh();
  }

  async function tryRefreshAuthToken(): Promise<string | null> {
    await hydrateOnce();
    return refresh();
  }

  async function getSessionInfo(): Promise<SessionInfo> {
    await hydrateOnce();
    return sessionInfoFromToken(
      session?.accessToken ?? null,
      session?.expiresAt ?? null,
    );
  }

  // ────────────────────────────────────────────────
  // Auth flows
  async function storeFromBackend(data: unknown) {
    const obj = data as { auth?: unknown; access_token?: string };
    const src = (obj.auth ?? data) as {
      access_token?: string;
      refresh_token?: string;
      expires_at?: number | string;
    };
    if (!src?.access_token) {
      logger.warn('[auth.storeFromBackend] No access_token found');
      return data;
    }
    const next: Session = {
      accessToken: src.access_token,
      refreshToken: src.refresh_token!,
      expiresAt: asAbsolute(Number(src.expires_at)),
    };
    await persist(next);
    return data;
  }

  async function signin(payload: SigninPayload) {
    // Always unauth — signin authenticates against email+password alone, and
    // the user may be signing into a *different* account from the current
    // guest session. Sending the guest Bearer was causing backend 401s on
    // web (silent failure from guest mode). After success storeFromBackend
    // swaps in the new authed session, replacing the guest one.
    const { unauthClient } = requireClients();
    const res = await unauthClient.post(`${AUTH_API}/signin`, payload);
    return storeFromBackend(res.data);
  }

  async function register(payload: RegisterPayload) {
    // Register from a guest IS an upgrade-this-guest path: send the guest
    // Bearer so backend converts the existing anonymous user into a real
    // account, preserving session_id, history, etc. This mirrors RN's
    // behavior. From a fully-unauthed state, send no token; backend creates
    // a fresh user.
    const info = await getSessionInfo();
    const { httpClient, unauthClient } = requireClients();
    const client = info.token ? httpClient : unauthClient;
    const res = await client.post(`${AUTH_API}/register`, payload);
    return storeFromBackend(res.data);
  }

  async function createGuestSession(metadata?: GuestSessionMetadata) {
    const { unauthClient } = requireClients();
    // Backend accepts an optional metadata blob — used by the embedded widget
    // to record which partner instance the guest was created from. Older
    // backends ignore the body; non-breaking for RN where no metadata is sent.
    const body = metadata ? { metadata } : undefined;
    const { data } = await unauthClient.post(`${AUTH_API}/guest`, body);
    return storeFromBackend(data);
  }

  async function setSessionFromExternal(tokens: {
    accessToken: string;
    refreshToken: string;
    expiresAt: number;
  }) {
    const next: Session = {
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      expiresAt: asAbsolute(tokens.expiresAt),
    };
    await persist(next);
  }

  async function clearAuthSession() {
    await persist(null);
    siblingStorageKeys.forEach(key => {
      if (key !== storageKey && storage.getString(key)) {
        logger.info(`[auth] Cleaning sibling storage key on logout: ${key}`);
        storage.remove(key);
      }
    });
    emit('sessionCleared');
  }

  async function logout() {
    try {
      // optional backend call
    } finally {
      storage.remove(storageKey);
      await clearAuthSession();
    }
  }

  async function deleteAccount() {
    try {
      const info = await getSessionInfo();
      if (!info.token) throw new Error('No authentication token available');
      const userId = info.userId;
      if (!userId) throw new Error('Unable to determine user ID from token');
      const { httpClient } = requireClients();
      await httpClient.delete(`/functions/v1/users/${userId}`);
    } finally {
      storage.clearAll?.();
      await clearAuthSession();
    }
  }

  async function requestPasswordReset(emailAddress: string, language?: string) {
    const { unauthClient } = requireClients();
    const appInfo = getAppInfo();
    return unauthClient.post(`${AUTH_API}/reset-password-request`, {
      emailAddress,
      language: language ?? 'en-US',
      appInfo,
    });
  }

  async function confirmPasswordReset(payload: {
    code: string;
    newPassword: string;
  }) {
    const { unauthClient } = requireClients();
    return unauthClient.post(`${AUTH_API}/reset-password-confirm`, payload);
  }

  function onAuthEvent(listener: AuthEventListener) {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }

  function attachClients(clients: {
    httpClient: AxiosInstance;
    unauthClient: AxiosInstance;
  }) {
    httpClient = clients.httpClient;
    unauthClient = clients.unauthClient;
  }

  return {
    attachClients,
    getAuthToken,
    tryRefreshAuthToken,
    getSessionInfo,
    signin,
    register,
    createGuestSession,
    setSessionFromExternal,
    logout,
    deleteAccount,
    clearAuthSession,
    requestPasswordReset,
    confirmPasswordReset,
    onAuthEvent,
  };
}
